/**
 * Request a manage-token access email — same auto-login as content site / engine links.
 * Link shape: {origin}{returnTo}?token=<manage_token> → emailTokenLogin → Supabase session.
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

const ACCESS_LINK_FN = "llc-access-link";

/** Shown when email is not in subscribers — no magic link is sent. */
export const ACCESS_LINK_UNKNOWN_EMAIL_MESSAGE =
  "Could not find your email. Are you sure you RSVPed?";

export type AccessLinkReason = "subscriber_not_found";

export function isSubscriberNotFoundReason(
  reason: AccessLinkReason | undefined,
): boolean {
  return reason === "subscriber_not_found";
}

export function accessLinkReturnTo(pathname: string, search = ""): string {
  const params = new URLSearchParams(search);
  params.delete("token");
  params.delete("return_to");
  const qs = params.toString();
  return pathname + (qs ? `?${qs}` : "");
}

export async function requestAccessLink(
  email: string,
  returnTo: string,
): Promise<{ ok: boolean; error?: string; reason?: AccessLinkReason }> {
  const base = String(SUPABASE_URL).replace(/\/$/, "");
  const res = await fetch(`${base}/functions/v1/${ACCESS_LINK_FN}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      apikey: SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({
      email: email.trim(),
      return_to: returnTo,
      site_origin: window.location.origin,
    }),
  });
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) {
    const code = typeof data.error === "string" ? data.error : "";
    if (code === "subscriber_not_found") {
      return {
        ok: false,
        error: ACCESS_LINK_UNKNOWN_EMAIL_MESSAGE,
        reason: "subscriber_not_found",
      };
    }
    return { ok: false, error: "Could not send link. Try again." };
  }
  return { ok: true };
}
